import PropTypes from 'prop-types';
import { path } from 'ramda';
import React, {Fragment, useCallback, useEffect, useState} from 'react';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';

import AppBar from '@material-ui/core/AppBar';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Switch from '@material-ui/core/Switch';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Toolbar from '@material-ui/core/Toolbar';
import common from '@material-ui/core/es/colors/common';
import grey from '@material-ui/core/es/colors/grey';
import { withStyles } from '@material-ui/styles';

import { readEndpoint } from 'redux-json-api';

import ApiUserRow from './ApiUserRow';
import { TablePagination } from "@material-ui/core";

let getUsersCount = 0;

const ApiUserList = ({
  classes,
  companies,
  readEndpoint,
}) => {
  const [company, setCompany] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [termFirstName, setTermFirstName] = useState(undefined);
  const [termLastName, setTermLastName] = useState(undefined);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [usersCount, setUsersCount] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getUsers(showInactive, company, page, pageSize);
  }, [showInactive, company, page, pageSize])

  const getUsers = (showInactive, company, page, pageSize, searchedTermFirstName = termFirstName, searchedTermLastName = termLastName) => {
    setLoading(true);

    let endpoint = `api-user?page=${page}&page_size=${pageSize}&filter[is-active__exact]=${showInactive ? 'False' : 'True'}` +
      `${company !== null ? `&filter[default-company__exact]=${company}` : ''}` +
      '&filter[deleted-at__isnull]=True';

    if (searchedTermFirstName) {
      const query = `&filter[first_name__icontains]=${searchedTermFirstName}`;

      endpoint += query
    }

    if (searchedTermLastName) {
      const query = `&filter[last_name__icontains]=${searchedTermLastName}`;

      endpoint += query
    }

    const myGetUsersCount = ++getUsersCount;

    readEndpoint(endpoint)
      .then(response => {
        if (getUsersCount === myGetUsersCount) {
          setUsers(response.body.data);
          setUsersCount(response.body.meta.pagination.count);
        }
      })
      .catch((error) => {
         console.error(`[TVV] ApiUserList.getUsers(showInactive=${showInactive}) error=${error}`);

         setLoading(false);
         setUsers([]);
         setPage(1);
         setUsersCount(1);
      })
      .finally(() => setLoading(false));
  }

  const debounceHandler = useCallback(
    debounce( (showInactive, company, page, pageSize, firstName, lastName) =>
      getUsers(
        showInactive,
        company,
        page,
        pageSize,
        firstName,
        lastName
      ), 1000)
    , []);

  return (
    <Fragment>
      <AppBar position="relative">
        <Toolbar variant="dense" className={classes.appBar}>
          <FormControlLabel
            classes={{label: classes.toggleLabel}}
            control={
              <Switch
                color="primary"
                checked={showInactive}
                onChange={() => setShowInactive(!showInactive)}
              />
            }
            label="Inactief"
          />

          <TextField
            className={classes.companySearchField}
            placeholder="Zoekterm voornaam..."
            value={termFirstName || ''}
            onChange={({target: {value}}) => {
              setTermFirstName((!value || value === '') ? undefined : value);
              debounceHandler(showInactive, company, page, pageSize, (!value || value === '') ? null : value, termLastName)
            }}
          />

          <TextField
            className={classes.companySearchField}
            placeholder="Zoekterm achternaam..."
            value={termLastName || ''}
            onChange={({target: {value}}) => {
              setTermLastName((!value || value === '') ? undefined : value);
              debounceHandler(showInactive, company, page, pageSize, termFirstName, (!value || value === '') ? null : value)
            }}
          />

          <Select
            className={classes.companySelectField}
            placeholder="Bedrijf..."
            value={company || ''}
            onChange={({target: {value}}) => setCompany(value === '' ? null : value)}
          >
            <MenuItem value=""><em>Geen bedrijfsfilter</em></MenuItem>

            {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.attributes.name}</MenuItem>)}
          </Select>
        </Toolbar>
      </AppBar>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Naam</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Bedrijf</TableCell>
            <TableCell>Rollen</TableCell>
            <TableCell/>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={6}>Bezig met laden...</TableCell>
            </TableRow>
          ) : users.map(user => <ApiUserRow key={`api-user-${user.id}`} user={user}/>)}
        </TableBody>
      </Table>

      {loading ? (
        <Fragment>
          <br/>

          <CircularProgress size={80} classes={{root: classes.loadingIndicator}}/>
        </Fragment>
      ) : (
        <TablePagination
          count={usersCount}
          page={page - 1} // TablePagination numerotation starts from 0
          rowsPerPage={pageSize}
          labelRowsPerPage={"Rijen per pagina"}
          onChangePage={(e, page) => setPage(page + 1)}
          onChangeRowsPerPage={(event) => setPageSize(event.target.value)}
        />
      )}
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  companies: path(['api', 'company', 'data'], state) || [],
});

const mapDispatchToProps = {
  readEndpoint,
};

ApiUserList.propTypes = {
  classes: PropTypes.shape({}),
  companies: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    attributes: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
  })).isRequired,
  readEndpoint: PropTypes.func.isRequired,
}
export default connect(mapStateToProps, mapDispatchToProps)(withStyles({
  appBar: {
    backgroundColor: grey[300],
  },
  companySearchField: {
    marginLeft: 20,
    width: 190,
  },
  companySelectField: {
    marginLeft: 20,
  },
  loadingIndicator: {
    margin: '0 auto',
  },
  toggleLabel: {
    color: common.black,
    opacity: 0.38,
    transition: 'opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  },
})(ApiUserList));
